import * as React from "react"
import { cn } from "@/lib/utils"

// Cache for already loaded image URLs to prevent flash on navigation
const loadedImages = new Set()

const AvatarContext = React.createContext({ showFallback: false, setShowFallback: () => {} })

const Avatar = React.forwardRef(({ className, ...props }, ref) => {
  // Default to NOT showing fallback - only show if image fails or has no src
  const [showFallback, setShowFallback] = React.useState(false)

  return (
    <AvatarContext.Provider value={{ showFallback, setShowFallback }}>
      <div
        ref={ref}
        className={cn("relative flex h-10 w-10 shrink-0 overflow-hidden rounded-full", className)}
        {...props}
      />
    </AvatarContext.Provider>
  )
})
Avatar.displayName = "Avatar"

const AvatarImage = React.forwardRef(({ className, src, onLoad, onError, ...props }, ref) => {
  const { setShowFallback } = React.useContext(AvatarContext)
  const [hasError, setHasError] = React.useState(false)

  // Check cache synchronously on mount
  React.useLayoutEffect(() => {
    if (!src) {
      setShowFallback(true)
      return
    }
    setHasError(false)
    // If already in cache, no need to show fallback
    if (loadedImages.has(src)) {
      setShowFallback(false)
    }
    // Don't show fallback while loading - let the image load
  }, [src, setShowFallback])

  if (!src || hasError) return null

  return (
    <img
      ref={ref}
      src={src}
      className={cn("aspect-square h-full w-full object-cover", className)}
      onLoad={(e) => {
        loadedImages.add(src)
        setShowFallback(false)
        onLoad?.(e)
      }}
      onError={(e) => {
        loadedImages.delete(src)
        setHasError(true)
        setShowFallback(true)
        onError?.(e)
      }}
      {...props}
    />
  )
})
AvatarImage.displayName = "AvatarImage"

const AvatarFallback = React.forwardRef(({ className, ...props }, ref) => {
  const { showFallback } = React.useContext(AvatarContext)

  // Only render fallback if explicitly needed (no src or error)
  if (!showFallback) return null

  return (
    <div
      ref={ref}
      className={cn(
        "flex h-full w-full items-center justify-center rounded-full bg-muted",
        className
      )}
      {...props}
    />
  )
})
AvatarFallback.displayName = "AvatarFallback"

export { Avatar, AvatarImage, AvatarFallback }
